import { getLoggerManager } from '@/shared/logging'

const EVENT_LOOP_SAMPLE_INTERVAL_MS = 50

export interface GltfRuntimePerformanceReport {
  readonly wallMs: number
  readonly longTaskSupported: boolean
  readonly longTaskCount: number
  readonly longTaskMs: number
  readonly longTaskMaxMs: number
  readonly eventLoopSampleCount: number
  readonly eventLoopLagMs: number
  readonly eventLoopLagP95Ms: number
  readonly eventLoopLagMaxMs: number
}

export interface GltfRuntimePerformanceMonitor {
  stop(): GltfRuntimePerformanceReport | undefined
}

function percentile(values: readonly number[], ratio: number): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((left, right) => left - right)
  return sorted[Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * ratio))] ?? 0
}

/** 仅在诊断日志开启时采样直接构建期间的主线程阻塞，结束后返回单条聚合数据。 */
export function startGltfRuntimePerformanceMonitor(
  enabled: boolean
): GltfRuntimePerformanceMonitor {
  if (!enabled) return { stop: () => undefined }

  const clock = getLoggerManager()
  const startedAt = clock.now()
  const eventLoopLagSamples: number[] = []
  const longTaskDurations: number[] = []
  let nextSampleAt = startedAt + EVENT_LOOP_SAMPLE_INTERVAL_MS
  let timer: ReturnType<typeof setTimeout> | undefined
  let observer: PerformanceObserver | undefined
  let stoppedReport: GltfRuntimePerformanceReport | undefined
  let stopped = false

  const recordEventLoopLag = (now: number) => {
    if (now < nextSampleAt) return
    eventLoopLagSamples.push(Math.max(0, now - nextSampleAt))
    nextSampleAt = now + EVENT_LOOP_SAMPLE_INTERVAL_MS
  }
  const sampleEventLoop = () => {
    recordEventLoopLag(clock.now())
    timer = setTimeout(sampleEventLoop, EVENT_LOOP_SAMPLE_INTERVAL_MS)
  }
  timer = setTimeout(sampleEventLoop, EVENT_LOOP_SAMPLE_INTERVAL_MS)

  const recordLongTasks = (entries: readonly PerformanceEntry[]) => {
    for (const entry of entries) {
      if (entry.startTime >= startedAt) longTaskDurations.push(entry.duration)
    }
  }
  const longTaskSupported =
    typeof PerformanceObserver !== 'undefined' &&
    PerformanceObserver.supportedEntryTypes?.includes('longtask') === true
  if (longTaskSupported) {
    try {
      observer = new PerformanceObserver((list) => recordLongTasks(list.getEntries()))
      observer.observe({ entryTypes: ['longtask'] })
    } catch {
      observer?.disconnect()
      observer = undefined
    }
  }

  return {
    stop: () => {
      if (stopped) return stoppedReport
      stopped = true
      if (timer !== undefined) clearTimeout(timer)
      const endedAt = clock.now()
      recordEventLoopLag(endedAt)
      if (observer) {
        recordLongTasks(observer.takeRecords())
        observer.disconnect()
      }
      stoppedReport = {
        wallMs: Math.max(0, endedAt - startedAt),
        longTaskSupported: observer !== undefined,
        longTaskCount: longTaskDurations.length,
        longTaskMs: longTaskDurations.reduce((total, duration) => total + duration, 0),
        longTaskMaxMs: Math.max(0, ...longTaskDurations),
        eventLoopSampleCount: eventLoopLagSamples.length,
        eventLoopLagMs: eventLoopLagSamples.reduce((total, lag) => total + lag, 0),
        eventLoopLagP95Ms: percentile(eventLoopLagSamples, 0.95),
        eventLoopLagMaxMs: Math.max(0, ...eventLoopLagSamples),
      }
      return stoppedReport
    },
  }
}
